import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../utils';

const statusChipVariants = cva(
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium',
  {
    variants: {
      tone: {
        neutral: 'border-border bg-muted text-muted-foreground',
        info: 'border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-900 dark:bg-sky-950 dark:text-sky-200',
        success:
          'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200',
        warning:
          'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200',
        danger: 'border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

export type StatusTone = NonNullable<VariantProps<typeof statusChipVariants>['tone']>;

const dotClass: Record<StatusTone, string> = {
  neutral: 'bg-muted-foreground',
  info: 'bg-sky-500',
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  danger: 'bg-red-500',
};

export type StatusChipProps = React.HTMLAttributes<HTMLSpanElement> &
  VariantProps<typeof statusChipVariants> & { dot?: boolean };

/** One-word state of a row (Invited, Joined, Expired…), colour-coded by tone. */
export function StatusChip({ className, tone, dot = true, children, ...props }: StatusChipProps) {
  const resolved: StatusTone = tone ?? 'neutral';
  return (
    <span className={cn(statusChipVariants({ tone: resolved }), className)} data-tone={resolved} {...props}>
      {dot ? <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', dotClass[resolved])} /> : null}
      {children}
    </span>
  );
}

export { statusChipVariants };
